import { createDataSourceExecution } from '@/api';
import { CDM_VERSIONS } from '@/constants';
import {
  Alert,
  Block,
  Button,
  Checkbox,
  Divider,
  Grid,
  Icon,
  Select,
  Spinner,
  Typography,
} from '@prometheus/components';
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  SecondaryContentWrapper,
} from '@prometheus/features';
import { DialogContext, UseDialogContext } from '@prometheus/hooks';
import { Status } from '@prometheus/types';
import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ExecutionModal: FC<{
  dataSourceId: string;
  onClose?: () => void;
  reloadList: () => void;
  isExecutionAllowed: boolean;
}> = ({ dataSourceId, onClose, reloadList, isExecutionAllowed = true }) => {
  const { t } = useTranslation();
  const { showDialog, hideDialog } =
    useContext<UseDialogContext>(DialogContext);
  const [status, setStatus] = useState(Status.INITIAL);
  const [state, setState] = useState({
    createCdmSchema: false,
    cdmVersion: 'V5_4',
    createResultsSchema: false,
  });
  const [executeAnyway, setExecuteAnyway] = useState(false);
  const handleChange = (fieldName: string) => (value: any) => {
    setState(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = () => {
    setStatus(Status.IN_PROGRESS);
    createDataSourceExecution(dataSourceId, state)
      .then(result => {
        reloadList();
        onClose?.();
        setStatus(Status.SUCCESS);
      })
      .catch(error => setStatus(Status.ERROR));
  };
  const confirmSubmit = e => {
    e.preventDefault();
    if (state.createCdmSchema) {
      showDialog<ConfirmationDialogProps>(ConfirmationDialog, {
        onSubmit: () => {
          handleSubmit();
        },
        onClose: hideDialog,
        text: t('messages.confirm_execution_rewrite'),
        confirmButtonText: t('buttons.continue'),
        variant: 'info',
      });
    } else {
      handleSubmit();
    }
  };
  const DISABLE =
    status === Status.IN_PROGRESS ||
    (!state.createCdmSchema && !state.createResultsSchema);
  const FORM_DISABLE = !executeAnyway && !isExecutionAllowed;

  return (
    <Grid container>
      <SecondaryContentWrapper>
        <Block>
          <form onSubmit={confirmSubmit}>
            <Grid container p={2} spacing={2}>
              {!isExecutionAllowed && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        {t('messages.configuration_already_in_progress')}
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography component="label" variant="body2">
                          <Checkbox
                            id="executeAnyway"
                            checked={executeAnyway}
                            size="small"
                            sx={{ mr: 1, mt: '-1px' }}
                            onChange={e => setExecuteAnyway(e.target.checked)}
                          />
                          {t('forms.proceed_anyway')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Grid item>
                  <Typography component="label" variant="subtitle1">
                    {t('forms.cdm_version')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Select
                    id="cdmVersion"
                    value={state.cdmVersion}
                    options={CDM_VERSIONS}
                    fullWidth
                    placeholder={t('forms.cdm_version_placeholder')}
                    onChange={handleChange('cdmVersion')}
                    disabled={!state.createCdmSchema || FORM_DISABLE}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography component="label" variant="body2">
                  <Checkbox
                    id="createCdmSchema"
                    checked={state.createCdmSchema}
                    size="small"
                    sx={{ mr: 1.5, mt: '-1px' }}
                    onChange={e =>
                      handleChange('createCdmSchema')(e.target.checked)
                    }
                    disabled={FORM_DISABLE}
                  />
                  {t('forms.create_cdm_schema')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography component="label" variant="body2">
                  <Checkbox
                    id="createResultsSchema"
                    checked={state.createResultsSchema}
                    size="small"
                    sx={{ mr: 1.5, mt: '-1px' }}
                    onChange={e =>
                      handleChange('createResultsSchema')(e.target.checked)
                    }
                    disabled={FORM_DISABLE}
                  />
                  {t('forms.create_results_schema')}
                </Typography>
                <Divider sx={{ mt: 2 }} />
              </Grid>

              <Grid item xs={12} textAlign="right">
                <Button
                  color="success"
                  variant="contained"
                  type="submit"
                  // onClick={handleSubmit}
                  disabled={DISABLE || FORM_DISABLE}
                  startIcon={
                    status === Status.IN_PROGRESS ? (
                      <Spinner size={15} />
                    ) : (
                      <Icon iconName="check" sx={{ fontSize: 18 }} />
                    )
                  }
                >
                  {t('buttons.create')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Block>
      </SecondaryContentWrapper>
    </Grid>
  );
};
